import { useCallback, useEffect, useRef, useState } from 'react'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { SendTokenOptions, SendTokenResult } from '@farcaster/miniapp-core/src/actions/SendToken'
import { getTransactionHashOrUserOpHash } from '@towns/userops'
import { Box, Button, Stack, Text } from '@ui'
import { useTokenMetadataForChainId } from 'api/lib/collectionMetadata'
import { providers } from 'ethers'
import { shortAddress, shortenHexString } from 'ui/utils/utils'
import {
    type Address,
    TransactionStatus,
    type TransferAssetTransactionContext,
    useTransferAssetTransaction,
} from 'use-towns-client'
import { formatUnits, getAddress, isAddress } from 'viem'

export type NormalizedSendTokenRequest = {
    caipToken: string
    chainId: number
    chainName: string
    assetType: 'erc20' | 'native'
    assetNamespace: string
    assetReference: string
    tokenAddress?: Address
    amount: bigint
    rawAmount: string
    recipientAddress: Address
    nativeSymbol: string
    nativeDecimals: number
}

export type MiniappSendTokenPrompt = NormalizedSendTokenRequest & {
    fromAddress: Address
}

type SendTokenPromptResolver = {
    resolve: (result: SendTokenResult) => void
    reject: (reason?: unknown) => void
}

type TransferAssetData = NonNullable<TransferAssetTransactionContext['data']>
type LoadProviderFn = (address: string) => Promise<unknown>

export const useMiniappSendTokenPrompt = () => {
    const [prompt, setPrompt] = useState<MiniappSendTokenPrompt | null>(null)
    const resolverRef = useRef<SendTokenPromptResolver | null>(null)

    const requestSendTokenApproval = useCallback((details: MiniappSendTokenPrompt) => {
        return new Promise<SendTokenResult>((resolve, reject) => {
            // Reject new requests if one is already pending to prevent promise leaks.
            // The caller should wait for the current request to complete.
            if (resolverRef.current) {
                reject(new Error('Another send token request is pending'))
                return
            }
            resolverRef.current = { resolve, reject }
            setPrompt(details)
        })
    }, [])

    const resolveSendTokenPrompt = useCallback((result: SendTokenResult) => {
        resolverRef.current?.resolve(result)
        resolverRef.current = null
        setPrompt(null)
    }, [])

    const rejectSendTokenPrompt = useCallback((reason?: Error) => {
        resolverRef.current?.reject(reason ?? new Error('Send token prompt closed'))
        resolverRef.current = null
        setPrompt(null)
    }, [])

    useEffect(() => {
        return () => {
            if (resolverRef.current) {
                resolverRef.current.reject(new Error('Miniapp closed'))
                resolverRef.current = null
            }
        }
    }, [])

    return {
        prompt,
        requestSendTokenApproval,
        resolveSendTokenPrompt,
        rejectSendTokenPrompt,
    }
}

export const useMiniappSendTokenController = ({
    loadProviderForAddress,
    privyAddress,
}: {
    loadProviderForAddress: LoadProviderFn
    privyAddress: string | null
}) => {
    const { prompt, requestSendTokenApproval, resolveSendTokenPrompt } = useMiniappSendTokenPrompt()
    const { transferAsset } = useTransferAssetTransaction()
    const [isProcessing, setIsProcessing] = useState(false)

    const handleCancel = useCallback(() => {
        resolveSendTokenPrompt({
            success: false,
            reason: 'rejected_by_user',
        })
    }, [resolveSendTokenPrompt])

    const handleApprove = useCallback(async () => {
        if (!prompt) {
            return
        }
        if (!privyAddress) {
            resolveSendTokenPrompt({
                success: false,
                reason: 'send_failed',
                error: {
                    error: 'signer_unavailable',
                    message: 'Privy wallet address is unavailable',
                },
            })
            return
        }

        setIsProcessing(true)
        try {
            if (prompt.assetType === 'erc20' && !prompt.tokenAddress) {
                throw new Error('Token address is required for ERC-20 transfers')
            }

            const eip1193Provider = await loadProviderForAddress(privyAddress)
            const web3Provider = new providers.Web3Provider(
                eip1193Provider as providers.ExternalProvider,
            )
            const signer = await web3Provider.getSigner(privyAddress)

            const transferData: TransferAssetData =
                prompt.assetType === 'native'
                    ? {
                          value: prompt.amount,
                          recipient: prompt.recipientAddress,
                      }
                    : {
                          contractAddress: prompt.tokenAddress as Address,
                          erc20Amount: prompt.amount,
                          recipient: prompt.recipientAddress,
                      }

            const context = await transferAsset({
                data: transferData,
                signer,
            })

            if (context?.status === TransactionStatus.Success) {
                const transactionHash = getTransactionHashOrUserOpHash(context.transaction)
                if (!transactionHash) {
                    throw new Error('Transaction hash is unavailable')
                }
                resolveSendTokenPrompt({
                    success: true,
                    send: {
                        transaction: transactionHash,
                    },
                })
            } else if (context?.status === TransactionStatus.Failed) {
                resolveSendTokenPrompt({
                    success: false,
                    reason: 'send_failed',
                    error: {
                        error: 'transaction_failed',
                        message: context.error?.message ?? 'Token transfer failed',
                    },
                })
            } else {
                throw new Error('Token transfer was not completed')
            }
        } catch (error) {
            if (isUserRejectedError(error)) {
                resolveSendTokenPrompt({
                    success: false,
                    reason: 'rejected_by_user',
                })
            } else {
                resolveSendTokenPrompt({
                    success: false,
                    reason: 'send_failed',
                    error: {
                        error: 'exception',
                        message: error instanceof Error ? error.message : 'Unknown error',
                    },
                })
            }
        } finally {
            setIsProcessing(false)
        }
    }, [loadProviderForAddress, privyAddress, prompt, resolveSendTokenPrompt, transferAsset])

    return {
        prompt,
        requestSendTokenApproval,
        isProcessing,
        handleCancel,
        handleApprove,
    }
}

type NormalizeArgs = {
    options: SendTokenOptions
    supportedChainIds: number[]
    nativeSymbol: string
    nativeDecimals: number
    resolveChainName?: (chainId: number) => string
}

type ParsedCaip19 = {
    chainNamespace: string
    chainReference: string
    assetNamespace: string
    assetReference: string
}

const parseCaip19 = (value: string): ParsedCaip19 => {
    const [chainPart, assetPart] = value.split('/')
    if (!chainPart || !assetPart) {
        throw new Error('Invalid token identifier format')
    }
    const [chainNamespace, chainReference] = chainPart.split(':')
    const [assetNamespace, assetReference] = assetPart.split(':')
    if (!chainNamespace || !chainReference || !assetNamespace || !assetReference) {
        throw new Error('Malformed CAIP-19 asset identifier')
    }
    return {
        chainNamespace,
        chainReference,
        assetNamespace,
        assetReference,
    }
}

export const normalizeSendTokenOptions = ({
    options,
    supportedChainIds,
    nativeSymbol,
    nativeDecimals,
    resolveChainName = (chainId) => `Chain ${chainId}`,
}: NormalizeArgs): NormalizedSendTokenRequest => {
    const token = options.token?.trim()
    if (!token) {
        throw new Error('actions.sendToken requires a token identifier')
    }
    if (!options.amount) {
        throw new Error('actions.sendToken requires an amount')
    }

    const amount = (() => {
        try {
            const parsed = BigInt(options.amount)
            if (parsed <= 0n) {
                throw new Error('Amount must be greater than zero')
            }
            return parsed
        } catch (error) {
            throw new Error('actions.sendToken amount must be a numeric string')
        }
    })()

    if (!options.recipientAddress) {
        throw new Error('actions.sendToken requires recipientAddress')
    }
    if (!isAddress(options.recipientAddress)) {
        throw new Error('actions.sendToken recipientAddress must be a valid address')
    }
    const recipientAddress = getAddress(options.recipientAddress)

    const parsed = parseCaip19(token)
    if (parsed.chainNamespace !== 'eip155') {
        throw new Error('actions.sendToken only supports eip155 chains')
    }
    const chainId = Number(parsed.chainReference)
    if (!Number.isInteger(chainId)) {
        throw new Error('Invalid chain reference in token identifier')
    }
    if (!supportedChainIds.includes(chainId)) {
        throw new Error(`Unsupported chain ${chainId}`)
    }

    let assetType: NormalizedSendTokenRequest['assetType'] = 'erc20'
    let tokenAddress: Address | undefined

    if (parsed.assetNamespace === 'erc20') {
        if (!isAddress(parsed.assetReference)) {
            throw new Error('actions.sendToken ERC-20 reference must be a contract address')
        }
        tokenAddress = getAddress(parsed.assetReference)
        assetType = 'erc20'
    } else if (parsed.assetNamespace === 'slip44' || parsed.assetNamespace === 'native') {
        assetType = 'native'
    } else {
        throw new Error(`Unsupported asset namespace ${parsed.assetNamespace}`)
    }

    return {
        caipToken: token,
        chainId,
        chainName: resolveChainName(chainId),
        assetType,
        assetNamespace: parsed.assetNamespace,
        assetReference: parsed.assetReference,
        tokenAddress,
        amount,
        rawAmount: options.amount,
        recipientAddress,
        nativeSymbol,
        nativeDecimals,
    }
}

const formatAmount = (amount: bigint, decimals?: number) => {
    if (typeof decimals !== 'number') {
        return undefined
    }
    try {
        return formatUnits(amount, decimals)
    } catch {
        return undefined
    }
}

export const MiniappSendTokenModal = ({
    prompt,
    isProcessing,
    onCancel,
    onConfirm,
}: {
    prompt: MiniappSendTokenPrompt | null
    isProcessing: boolean
    onCancel: () => void
    onConfirm: () => void
}) => {
    if (!prompt) {
        return null
    }

    if (prompt.assetType === 'erc20' && prompt.tokenAddress) {
        return (
            <MiniappErc20SendTokenModal
                prompt={prompt}
                isProcessing={isProcessing}
                onCancel={onCancel}
                onConfirm={onConfirm}
            />
        )
    }

    return (
        <MiniappSendTokenModalContent
            prompt={prompt}
            isProcessing={isProcessing}
            onCancel={onCancel}
            onConfirm={onConfirm}
        />
    )
}

const MiniappErc20SendTokenModal = ({
    prompt,
    isProcessing,
    onCancel,
    onConfirm,
}: {
    prompt: MiniappSendTokenPrompt
    isProcessing: boolean
    onCancel: () => void
    onConfirm: () => void
}) => {
    const { data } = useTokenMetadataForChainId(prompt.tokenAddress as Address, prompt.chainId)
    return (
        <MiniappSendTokenModalContent
            prompt={prompt}
            isProcessing={isProcessing}
            tokenMetadata={data}
            onCancel={onCancel}
            onConfirm={onConfirm}
        />
    )
}

const MiniappSendTokenModalContent = ({
    prompt,
    isProcessing,
    onCancel,
    onConfirm,
    tokenMetadata,
}: {
    prompt: MiniappSendTokenPrompt
    isProcessing: boolean
    onCancel: () => void
    onConfirm: () => void
    tokenMetadata?: ReturnType<typeof useTokenMetadataForChainId>['data']
}) => {
    const decimals =
        prompt.assetType === 'native' ? prompt.nativeDecimals : tokenMetadata?.data.decimals
    const symbol = prompt.assetType === 'native' ? prompt.nativeSymbol : tokenMetadata?.data.symbol
    const formattedAmount = formatAmount(prompt.amount, decimals)

    const tokenDisplayName = (() => {
        if (symbol) {
            return symbol
        }
        if (prompt.assetType === 'native') {
            return prompt.nativeSymbol
        }
        if (prompt.tokenAddress) {
            return shortenHexString(prompt.tokenAddress)
        }
        return prompt.assetReference
    })()

    const amountDisplay = formattedAmount
        ? `${formattedAmount} ${symbol ?? ''}`.trim()
        : `${prompt.rawAmount} units`

    return (
        <ModalContainer
            disableBackgroundClick
            padding="lg"
            touchTitle="Send token"
            onHide={onCancel}
        >
            <Stack gap="lg">
                <Stack gap="sm">
                    <Text fontWeight="strong">Miniapp requests a token transfer</Text>
                    <Text color="gray2" fontSize="sm">
                        Tokens will be sent from {shortAddress(prompt.fromAddress)} on
                        <br />
                        {prompt.chainName}. Review the details below before confirming.
                    </Text>
                </Stack>

                <Stack gap background="level1" padding="md" borderRadius="md">
                    <DetailRow label="From" value={shortAddress(prompt.fromAddress)} />
                    <DetailRow label="To" value={shortAddress(prompt.recipientAddress)} />
                    <DetailRow label="Chain" value={`${prompt.chainName} (${prompt.chainId})`} />
                    <DetailRow label="Token" value={tokenDisplayName} />
                    <DetailRow label="Amount" value={amountDisplay} />
                    {formattedAmount && (
                        <DetailRow
                            mono
                            label="Raw amount"
                            value={`${prompt.rawAmount} (${prompt.amount.toString()} base units)`}
                        />
                    )}
                    {prompt.assetType === 'erc20' && prompt.tokenAddress && (
                        <DetailRow
                            label="Token contract"
                            value={shortAddress(prompt.tokenAddress)}
                        />
                    )}
                    <DetailRow mono label="CAIP asset" value={prompt.caipToken} />
                </Stack>

                <Stack gap horizontal justifyContent="end">
                    <Button tone="transparent5" disabled={isProcessing} onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button tone="cta1" disabled={isProcessing} onClick={onConfirm}>
                        {isProcessing ? 'Sending...' : 'Confirm & Send'}
                    </Button>
                </Stack>
            </Stack>
        </ModalContainer>
    )
}

const DetailRow = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => {
    return (
        <Stack horizontal justifyContent="spaceBetween" alignItems="center" gap="md">
            <Text color="gray2">{label}</Text>
            <Box
                textAlign="right"
                style={{
                    fontFamily: mono ? 'monospace' : undefined,
                    maxWidth: '60%',
                    overflowWrap: 'anywhere',
                }}
            >
                {value}
            </Box>
        </Stack>
    )
}

const isUserRejectedError = (error: unknown) => {
    if (!error || typeof error !== 'object') {
        return false
    }
    const code = (error as { code?: number | string }).code
    return code === 4001 || code === 'ACTION_REJECTED'
}
